import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
	Container,
	Paper,
	Typography,
	Box,
	Card,
	Button,
	IconButton,
	Snackbar,
	Alert,
} from '@mui/material';
import {
	Send as SendIcon,
	ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import QueueIcon from '@mui/icons-material/HourglassEmpty';
import echoLogo from '../../assets/newEchoLogo.png';
import {
	createConversation,
	sendCustomerMessage,
	getMessages,
	markMessagesAsRead,
} from '../../service/messaging';
import { messagingWebSocket } from '../../service/websocket';
import { listChatAvailabilityByAppName } from '../../service/chat';


// ============== COUNSELLOR CHAT LINES ==============
const counsellorLines = [
	{
		id: 1,
		name: 'echoCounsellor Line 1',
		subtitle: 'Talk about anything on your mind',
		avatar: 'icon',
		color: '#22c55e',
		 status: 'available',
		// status: 'offline',
		waitTime: null,
		doctorId: 15, // Dr. Lalita
	},
	{
		id: 2,
		name: 'echoCounsellor Line 2',
		subtitle: 'Safe space for any concern',
		avatar: 'icon',
		color: '#14b8a6',
		status: 'busy',
		// status: 'offline',
		waitTime: null,
		doctorId: 15, // Dr. Lalita
	},
	{
		id: 3,
		name: 'echoCounsellor Line 3',
		subtitle: 'Confidential & judgement-free',
		avatar: 'icon',
		color: '#8b5cf6',
		 status: 'busy',
		// status: 'offline',
		waitTime: null,
		doctorId: 15, // Dr. Lalita
	},
	{
		id: 4,
		name: 'echoCounsellor Line 4',
		subtitle: 'We listen, you heal',
		avatar: 'icon',
		color: '#f59e0b',
		status: 'busy',
		// status: 'offline',
		waitTime: null,
		doctorId: 15, // Dr. Lalita
	},
];

// Generic chat questionnaire flow
const genericQuestions = [
	{
		id: 'q1',
		text: "I'm here to help while our counsellors are busy. What's been on your mind lately?",
		type: 'choice',
		options: ['Feeling stressed or anxious', 'Relationship concerns', 'Work or career pressure', 'Sleep or health issues', 'Feeling low or unmotivated', 'Just need someone to talk to'],
	},
	{
		id: 'q2',
		text: 'How would you describe what you are feeling right now?',
		type: 'choice',
		options: ['Anxious or Worried', 'Sad or Low', 'Stressed or Overwhelmed', 'Angry or Frustrated', 'Confused or Lost', 'Something else'],
	},
	{
		id: 'q3',
		text: 'How long have you been experiencing this?',
		type: 'choice',
		options: ['Just today', 'A few days', 'A few weeks', 'More than a month', 'On and off for a while'],
	},
	{
		id: 'q4',
		text: 'Is this affecting your daily activities like work, sleep, or relationships?',
		type: 'choice',
		options: ['Yes, significantly', 'Somewhat', 'Not much', 'I\'m not sure'],
	},
	{
		id: 'q5',
		text: 'Have you spoken to anyone about this before — a friend, family member, or professional?',
		type: 'choice',
		options: ['Yes, a professional', 'Yes, friends/family', 'No, this is my first time', 'I prefer not to say'],
	},
];

const genericSuggestions = [
	{ icon: '🧘', label: 'Try Breathing Exercise', route: '/daily-breathing' },
	{ icon: '🎧', label: 'Sound Therapy', route: '/echo-sounds' },
	{ icon: '📝', label: 'Gratitude Journal', route: '/gratitude-checkin' },
	{ icon: '📅', label: 'Book Appointment', route: '/consult' },
];

const parseServerTimestamp = (ts) => {
	if (!ts) return new Date();
	if (ts instanceof Date) return ts;
	const match = String(ts).match(
		/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/
	);
	if (!match) return new Date(ts); // fallback for unexpected formats
	const [, y, mo, d, h, mi, s] = match.map(Number);
	return new Date(y, mo - 1, d, h, mi, s); // local time, no conversion
};

const Converse = () => {
	const navigate = useNavigate();

	// Lobby & Generic Chat states
	const [showLobby, setShowLobby] = useState(true);
	const [chatLines, setChatLines] = useState(counsellorLines);
	const [genericChatOpen, setGenericChatOpen] = useState(false);
	const [genericMessages, setGenericMessages] = useState([]);
	const [genericInput, setGenericInput] = useState('');
	const [genericTyping, setGenericTyping] = useState(false);
	const [genericQuestionIdx, setGenericQuestionIdx] = useState(0);
	const [genericAnswers, setGenericAnswers] = useState([]);
	const [genericDone, setGenericDone] = useState(false);
	const [queueJoined, setQueueJoined] = useState(false);
	const [liveChatDoctor, setLiveChatDoctor] = useState(null);
	const [liveChatOpen, setLiveChatOpen] = useState(false);
	const [liveChatMessages, setLiveChatMessages] = useState([]);
	const [liveChatInput, setLiveChatInput] = useState('');
	const [liveChatTyping, setLiveChatTyping] = useState(false);
	const genericEndRef = useRef(null);
	const liveChatEndRef = useRef(null);
	const typingTimeoutRef = useRef(null);
	const typingSendTimeoutRef = useRef(null);
	const [chatRooms, setChatRooms] = useState([]);
	// Messaging API states
	const [conversationId, setConversationId] = useState(null);
	const [customerId, setCustomerId] = useState(null);
	const [wsConnected, setWsConnected] = useState(false);
	const storedUser = JSON.parse(localStorage.getItem("user") || "null");
	const [newMessageAlert, setNewMessageAlert] = useState(false);
	const [newMessageText, setNewMessageText] = useState('');
	const notificationSoundRef = useRef(null);

	// Create notification sound using Web Audio API
	const playNotificationSound = useCallback(() => {
		try {
			const audioContext = new (window.AudioContext || window.webkitAudioContext)();
			const oscillator = audioContext.createOscillator();
			const gainNode = audioContext.createGain();

			oscillator.connect(gainNode);
			gainNode.connect(audioContext.destination);

			oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
			oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1);
			oscillator.type = 'sine';

			gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
			gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);

			oscillator.start(audioContext.currentTime);
			oscillator.stop(audioContext.currentTime + 0.4);
		} catch (e) {
			console.log('Could not play notification sound:', e);
		}
	}, []);

	// Request browser notification permission on mount
	useEffect(() => {
		if ('Notification' in window && Notification.permission === 'default') {
			Notification.requestPermission();
		}
	}, []);

	// Flash document title when new message arrives while tab is not focused
	useEffect(() => {
		let interval;
		if (newMessageAlert && document.hidden) {
			const originalTitle = document.title;
			let toggle = false;
			interval = setInterval(() => {
				document.title = toggle ? '💬 New message!' : originalTitle;
				toggle = !toggle;
			}, 1000);

			const handleFocus = () => {
				clearInterval(interval);
				document.title = originalTitle;
			};
			window.addEventListener('focus', handleFocus);
			return () => {
				clearInterval(interval);
				document.title = originalTitle;
				window.removeEventListener('focus', handleFocus);
			};
		}
		return () => clearInterval(interval);
	}, [newMessageAlert]);

	// Show browser notification when tab is not focused
	const showBrowserNotification = useCallback((message) => {
		if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
			const notification = new Notification('New message from Counsellor', {
				body: message,
				icon: echoLogo,
				tag: 'echoshine-chat',
			});
			notification.onclick = () => {
				window.focus();
				notification.close();
			};
			// Auto-close after 5 seconds
			setTimeout(() => notification.close(), 5000);
		}
	}, []);

	// Handle new counsellor message notification
	const notifyNewMessage = useCallback((messageText) => {
		playNotificationSound();
		showBrowserNotification(messageText);
		setNewMessageText(messageText);
		setNewMessageAlert(true);
	}, [playNotificationSound, showBrowserNotification]);

	console.log("liveChatMessages", liveChatMessages)


	// Get customer ID from localStorage
	useEffect(() => {
		if (storedUser?.id) {
			setCustomerId(storedUser?.id);
		} else {
			// For testing without login, use a test customer ID
			setCustomerId(1);
			console.log('Using test customer ID: 1');
		}
	}, []);

	useEffect(() => {
		fetchChatAvailability();
	}, []);

	const fetchChatAvailability = async () => {
		try {
			const data = await listChatAvailabilityByAppName("echoshine");
			console.log("Chat Availability:", data);

			const updatedLines = counsellorLines.map((line) => {
				const matchedRoom = data.find(
					(room) => room.chat_room === line.id
				);

				// ❌ If no record OR counsellor inactive
				if (!matchedRoom || matchedRoom.status !== "active") {
					return {
						...line,
						doctorId: null,
						status: "offline",
					};
				}

				// ✅ Determine status using chat count
				let status = "busy";
				if (matchedRoom.maximum_chat > matchedRoom.active_chat) {
					status = "available";
				}

				return {
					...line,
					doctorId: matchedRoom.counsellor_id,
					status: status,
				};
			});

			setChatLines(updatedLines);
		} catch (error) {
			console.error("Failed to fetch chat availability:", error);
		}
	};

	// Setup WebSocket for real-time messaging
	const setupWebSocket = (convId, custId) => {
		// Clear any existing handlers before adding new ones
		messagingWebSocket.clearHandlers();

		// Handle incoming messages from WebSocket
		messagingWebSocket.onMessage((data) => {
			if (data.type === 'new_message') {
				// Only handle counsellor messages via WS (customer msgs added locally on send)
				if (data.sender_type !== 'customer') {
					const newMessage = {
						from: 'bot',
						text: data.message_text,
						timestamp: data.created_at,
					};

					setLiveChatMessages(prev => [...prev, newMessage]);
					setLiveChatTyping(false); // Stop typing indicator when message arrives
					notifyNewMessage(data.message_text);
				}

				// Mark as read
				markMessagesAsRead(convId);
			} else if (data.type === 'typing') {
				// Counsellor is typing
				if (data.sender_type !== 'customer') {
					setLiveChatTyping(true);
					// Auto-hide typing after 3 seconds if no new typing event
					if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
					typingTimeoutRef.current = setTimeout(() => {
						setLiveChatTyping(false);
					}, 3000);
				}
			} else if (data.type === 'message_history') {
				// Initial message history
				const transformedMessages = (data.messages || []).map(msg => ({
					from: msg.sender_type === 'customer' ? 'user' : 'bot',
					text: msg.message_text,
					timestamp: msg.created_at,
				}));
				setLiveChatMessages(transformedMessages);
			}
		});

		// Handle connection status changes
		messagingWebSocket.onConnectionChange((status) => {
			setWsConnected(status === 'connected');
			if (status === 'connected') {
				console.log('WebSocket connected - real-time messaging active');
			} else if (status === 'disconnected') {
				console.log('WebSocket disconnected - attempting to reconnect...');
			} else if (status === 'failed') {
				console.error('WebSocket connection failed');
			}
		});

		// Connect to WebSocket
		messagingWebSocket.connect(convId, custId);
	};

	// ============== LOBBY & GENERIC CHAT HANDLERS ==============
	const allBusy = chatLines.every(l => l.status === 'busy');

	const handleConnectCounsellor = async (line) => {
		if (line.status === 'busy' || !line.doctorId) return;

		if (!customerId) {
			alert('Please wait, loading...');
			return;
		}

		try {
			setLiveChatOpen(true);
			setShowLobby(false);
			setLiveChatDoctor(line);
			setLiveChatMessages([{ from: 'bot', text: 'Connecting you to counsellor...' }]);

			// Create conversation with Dr. Lalita (doctor_id: 15)
			const result = await createConversation(customerId, line.doctorId);
			setConversationId(result.conversation_id);
			console.log('Conversation created:', result.conversation_id);

			// Store in localStorage for global notification polling
			localStorage.setItem('activeConversationId', result.conversation_id);

			// Fetch existing messages
			const messagesData = await getMessages(result.conversation_id);
			const transformedMessages = messagesData.messages.map(msg => ({
				from: msg.sender_type === 'customer' ? 'user' : 'bot',
				text: msg.message_text,
				timestamp: msg.created_at,
			}));

			// Add welcome message if no previous messages
			if (transformedMessages.length === 0) {
				transformedMessages.unshift({
					from: 'bot',
					text: `Welcome! 💜 You're now connected with ${line.name}. This is a safe and confidential space. How can I help you today?`
				});
			}

			setLiveChatMessages(transformedMessages);
			setLiveChatInput('');

			// Setup WebSocket for real-time messaging
			setupWebSocket(result.conversation_id, customerId);
			await markMessagesAsRead(result.conversation_id);

		} catch (error) {
			console.error('Error connecting to counsellor:', error);
			setLiveChatMessages([{ from: 'bot', text: '❌ Failed to connect. Please try again.' }]);
		}
	};

	const liveCounsellorResponses = [
		"I hear you, and I want you to know that what you're feeling is completely valid. Can you tell me more about what's been on your mind?",
		"Thank you for sharing that with me. It takes courage to open up. Let's explore this together — what do you think triggered these feelings?",
		"That sounds really challenging. Remember, it's okay to not have all the answers right now. What would feel most supportive for you at this moment?",
		"I appreciate you trusting me with this. Many people experience similar feelings. Have you noticed any patterns in when these feelings come up?",
		"You're doing great by reaching out. Self-awareness is the first step toward positive change. What coping strategies have you tried so far?",
		"I understand. Let's take this one step at a time. Would you like to try a quick grounding exercise together, or would you prefer to keep talking?",
		"That's a really insightful observation about yourself. How does it feel to acknowledge that?",
		"It's completely normal to feel that way. Let's work through this together. What would a good outcome look like for you?",
	];

	const handleSendLiveChat = async () => {
		const trimmed = liveChatInput.trim();
		if (!trimmed || !conversationId) return;

		setLiveChatInput('');

		// Immediately show the customer's own message locally
		const localMsg = {
			from: 'user',
			text: trimmed,
			timestamp: new Date(),
		};
		setLiveChatMessages(prev => [...prev, localMsg]);

		try {
			// Send message to counsellor portal API
			const senderName = storedUser?.name || '';
			await sendCustomerMessage(conversationId, trimmed, customerId, senderName);
			console.log('Message sent to counsellor');
		} catch (error) {
			console.error('Error sending message:', error);
			setLiveChatMessages(prev => [
				...prev,
				{ from: 'bot', text: '❌ Failed to send message. Please try again.' }
			]);
		}
	};

	const handleCloseLiveChat = () => {
		// Disconnect WebSocket
		messagingWebSocket.disconnect();
		setWsConnected(false);

		setLiveChatOpen(false);
		setLiveChatDoctor(null);
		setLiveChatMessages([]);
		setLiveChatTyping(false);
		setConversationId(null);
		setShowLobby(true);
		if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
		if (typingSendTimeoutRef.current) clearTimeout(typingSendTimeoutRef.current);
	};

	useEffect(() => {
		if (liveChatEndRef.current) liveChatEndRef.current.scrollIntoView({ behavior: 'smooth' });
	}, [liveChatMessages, liveChatTyping]);

	// Polling fallback: fetch new messages every 3 seconds when live chat is open
	useEffect(() => {
		let pollInterval;
		if (liveChatOpen && conversationId) {
			pollInterval = setInterval(async () => {
				try {
					const messagesData = await getMessages(conversationId);
					const fetched = (messagesData.messages || []).map(msg => ({
						from: msg.sender_type === 'customer' ? 'user' : 'bot',
						text: msg.message_text,
						timestamp: msg.created_at,
					}));

					setLiveChatMessages(prev => {
						// Only update if there are new messages (compare count)
						if (fetched.length > prev.length) {
							// Find new counsellor messages to notify
							const newMsgs = fetched.slice(prev.length);
							newMsgs.forEach(msg => {
								if (msg.from === 'bot') {
									notifyNewMessage(msg.text);
								}
							});
							return fetched;
						}
						return prev;
					});

					await markMessagesAsRead(conversationId);
				} catch (err) {
					console.error('Polling error:', err);
				}
			}, 3000);
		}
		return () => clearInterval(pollInterval);
	}, [liveChatOpen, conversationId, notifyNewMessage]);

	// Cleanup WebSocket on unmount
	useEffect(() => {
		return () => {
			messagingWebSocket.disconnect();
		};
	}, []);

	// Generic chat handlers
	const handleStartGenericChat = () => {
		setGenericChatOpen(true);
		setShowLobby(false);
		setGenericQuestionIdx(0);
		setGenericAnswers([]);
		setGenericDone(false);
		setQueueJoined(false);
		setGenericMessages([
			{ from: 'bot', text: '👋 Hi there! All our counsellors are currently assisting others.' },
			{ from: 'bot', text: "While you wait, I'd like to understand how you're feeling so we can help you better." },
			{ from: 'bot', text: genericQuestions[0].text, questionData: genericQuestions[0] },
		]);
	};

	const handleGenericChoice = (option) => {
		const currentQ = genericQuestions[genericQuestionIdx];
		setGenericMessages(prev => [...prev, { from: 'user', text: option }]);
		setGenericAnswers(prev => [...prev, { question: currentQ.text, answer: option }]);
		setGenericTyping(true);

		const nextIdx = genericQuestionIdx + 1;
		setTimeout(() => {
			setGenericTyping(false);
			if (nextIdx < genericQuestions.length) {
				setGenericQuestionIdx(nextIdx);
				setGenericMessages(prev => [...prev, { from: 'bot', text: genericQuestions[nextIdx].text, questionData: genericQuestions[nextIdx] }]);
			} else {
				setGenericDone(true);
				setGenericMessages(prev => [
					...prev,
					{ from: 'bot', text: "Thank you for sharing. Based on what you've told me, here's what I recommend:" },
					{ from: 'bot', text: '__suggestions__' },
					{ from: 'bot', text: 'You can also join the queue to speak with a counsellor as soon as one becomes available.' },
				]);
			}
		}, 1000);
	};

	const handleGenericSend = () => {
		const trimmed = genericInput.trim();
		if (!trimmed) return;
		const currentQ = genericQuestions[genericQuestionIdx];
		setGenericMessages(prev => [...prev, { from: 'user', text: trimmed }]);
		setGenericInput('');
		setGenericAnswers(prev => [...prev, { question: currentQ.text, answer: trimmed }]);
		setGenericTyping(true);

		const nextIdx = genericQuestionIdx + 1;
		setTimeout(() => {
			setGenericTyping(false);
			if (nextIdx < genericQuestions.length) {
				setGenericQuestionIdx(nextIdx);
				setGenericMessages(prev => [...prev, { from: 'bot', text: genericQuestions[nextIdx].text, questionData: genericQuestions[nextIdx] }]);
			} else {
				setGenericDone(true);
				setGenericMessages(prev => [
					...prev,
					{ from: 'bot', text: "Thank you for sharing. Based on what you've told me, here's what I recommend:" },
					{ from: 'bot', text: '__suggestions__' },
					{ from: 'bot', text: 'You can also join the queue to speak with a counsellor as soon as one becomes available.' },
				]);
			}
		}, 1000);
	};

	const handleJoinQueue = () => {
		setQueueJoined(true);
		setGenericMessages(prev => [
			...prev,
			{ from: 'bot', text: "✅ You've been added to the queue! A counsellor will connect with you shortly. In the meantime, feel free to explore our self-help tools above." },
		]);
	};

	const handleCloseGenericChat = () => {
		setGenericChatOpen(false);
		setGenericMessages([]);
		setGenericInput('');
		setGenericQuestionIdx(0);
		setGenericAnswers([]);
		setGenericDone(false);
		setQueueJoined(false);
		setShowLobby(true);
	};

	useEffect(() => {
		if (genericEndRef.current) genericEndRef.current.scrollIntoView({ behavior: 'smooth' });
	}, [genericMessages, genericTyping]);

	const getStatusColor = (status) => {
		if (status === "available") return "#22c55e";
		if (status === "busy") return "#f59e0b";
		return "#9e9e9e";
	};

	const getStatusLabel = (status) => {
		if (status === "available") return "Available";
		if (status === "busy") return "Busy";
		return "Offline";
	};

	// ============== LOBBY VIEW ==============
	const renderLobby = () => (
		<Box sx={{ width: '100%' }}>
			{/* Header - matching Care page style, full width */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2, md: 3 }, justifyContent: 'space-between', px: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
				<Box>
					<Typography variant="h6" sx={{
						fontWeight: 700,
						background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
						WebkitBackgroundClip: 'text',
						WebkitTextFillColor: 'transparent',
					}}>
						24/7 Chat Support
					</Typography>
					<Typography variant="body2" sx={{ color: '#6A73A8', fontSize: { xs: 12, sm: 13 } }}>
						Confidential support, anytime you need it
					</Typography>
				</Box>
				<IconButton
					onClick={() => navigate(-1)}
					sx={{
						backgroundColor: '#F0F5FF',
						width: { xs: 28, sm: 30, md: 40 },
						height: { xs: 28, sm: 30, md: 40 },
						borderRadius: '50%',
						color: '#0B2D6B',
						'&:hover': { backgroundColor: '#E0EBFF' }
					}}
				>
					<ArrowBackIcon sx={{ fontSize: { xs: '20px', sm: '22px' } }} />
				</IconButton>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 4 } }}>
				{/* Subheader */}
				<Typography sx={{ color: '#6A73A8', fontSize: { xs: 13, sm: 14 }, mb: 2, textAlign: 'center' }}>
					Select an available line to connect with a counsellor instantly
				</Typography>

				{/* Chat Lines - 2x2 Grid */}
				<Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
					{chatLines.map((line) => (
						<Card
							key={line.id}
							onClick={() => handleConnectCounsellor(line)}
							sx={{
								flex: '1 1 calc(50% - 12px)', maxWidth: 'calc(50% - 12px)',
								minWidth: { xs: '100%', sm: 'calc(50% - 12px)' },
								p: 2, borderRadius: 3, cursor: line.status === 'available' ? 'pointer' : 'default',
								border: '1px solid', borderColor: line.status === 'available' ? line.color : '#e0e0e0',
								background: line.status === 'available'
									? `linear-gradient(135deg, ${line.color}08, ${line.color}12)`
									: '#fafafa',
								transition: 'all 0.2s',
								opacity: line.status === 'busy' ? 0.7 : 1,
								'&:hover': line.status === 'available' ? {
									transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${line.color}30`,
								} : {},
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
								<Box sx={{
									width: 44, height: 44, borderRadius: '12px',
									background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
									opacity: line.status === 'busy' ? 0.6 : 1,
									display: 'flex', alignItems: 'center', justifyContent: 'center',
								}}>
									<SupportAgentIcon sx={{ fontSize: 24, color: '#fff' }} />
								</Box>
								<Box sx={{ flex: 1 }}>
									<Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1A2332' }}>
										{line.name}
									</Typography>
									<Typography sx={{ fontSize: 11, color: '#A0A8B8', lineHeight: 1.3 }}>
										{line.subtitle}
									</Typography>
								</Box>
								<FiberManualRecordIcon sx={{ fontSize: 10, color: getStatusColor(line.status) }} />

								<Typography sx={{ fontSize: 12, fontWeight: 600, color: getStatusColor(line.status) }}>
									{getStatusLabel(line.status)}
								</Typography>
							</Box>
						</Card>
					))}
				</Box>

				{/* All Busy Banner + Generic Chat CTA */}
				{/* {allBusy && ( */}
				{chatLines.every(line => line.status === "offline") && (

					<Card sx={{
						width: '100%', p: 3, borderRadius: 3, textAlign: 'center',
						background: 'linear-gradient(135deg, #FFF7ED, #FEF3C7)',
						border: '1px solid #FDE68A', mb: 3,
					}}>
						<Typography sx={{ fontSize: 28, mb: 1 }}>💬</Typography>
						<Typography sx={{ fontWeight: 700, fontSize: 16, color: '#92400E', mb: 0.5 }}>
							All counsellors are currently busy
						</Typography>
						<Typography sx={{ fontSize: 13, color: '#B45309', mb: 2 }}>
							Chat with our AI assistant — we'll understand your needs and connect you when a counsellor is free.
						</Typography>
						<Button
							variant="contained"
							onClick={handleStartGenericChat}
							startIcon={<HeadsetMicIcon />}
							sx={{
								background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
								textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 3,
								'&:hover': { background: 'linear-gradient(135deg, #16a34a, #0d9488, #7c3aed)' },
							}}
						>
							Start Chat Now
						</Button>
					</Card>
				)}

			</Box>
		</Box>
	);

	// ============== GENERIC CHAT VIEW ==============
	const renderGenericChat = () => (
		<Paper elevation={0} sx={{
			width: '100%', maxWidth: 700, height: '80vh', display: 'flex', flexDirection: 'column',
			borderRadius: '20px', overflow: 'hidden', mx: 'auto',
			background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
		}}>
			{/* Header */}
			<Box sx={{
				padding: '14px 22px',
				background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
				color: '#fff', display: 'flex', alignItems: 'center', gap: '10px',
			}}>
				<Box
					component="img"
					src={echoLogo}
					alt="echoShine"
					sx={{
						width: 38, height: 38, borderRadius: '10px', objectFit: 'cover',
					}}
				/>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700 }}>
						echoShine Support
					</Typography>
					<Typography sx={{ fontSize: 11, opacity: 0.85 }}>
						<Box component="span" sx={{ width: 7, height: 7, background: '#4ADE80', borderRadius: '50%', display: 'inline-block', mr: 0.5 }} />
						AI Assistant · Here to help
					</Typography>
				</Box>
				<IconButton onClick={handleCloseGenericChat} sx={{ color: '#fff' }}>
					<ArrowBackIcon />
				</IconButton>
			</Box>

			{/* Messages */}
			<Box sx={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '12px', background: '#FAFCFB' }}>
				{genericMessages.map((msg, idx) => {
					// Suggestions card
					if (msg.text === '__suggestions__') {
						return (
							<Box key={idx} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignSelf: 'flex-start', maxWidth: '90%' }}>
								{genericSuggestions.map((s, i) => (
									<Button
										key={i}
										variant="outlined"
										onClick={() => navigate(s.route)}
										sx={{
											textTransform: 'none', borderRadius: '12px', fontSize: 12, fontWeight: 600,
											borderColor: '#22c55e', color: '#1A2332',
											'&:hover': { borderColor: '#14b8a6', background: '#f0fdf4' },
										}}
									>
										{s.icon} {s.label}
									</Button>
								))}
							</Box>
						);
					}

					// Choice options
					const isLastBotMsg = msg.from === 'bot' && msg.questionData?.type === 'choice'
						&& idx === genericMessages.length - 1 && !genericDone;

					return (
						<Box key={idx}>
							<Box sx={{
								maxWidth: '80%', padding: '11px 15px', fontSize: 13, lineHeight: 1.5,
								animation: 'chatFadeIn 0.3s ease-out',
								'@keyframes chatFadeIn': {
									from: { opacity: 0, transform: 'translateY(14px)' },
									to: { opacity: 1, transform: 'translateY(0)' },
								},
								...(msg.from === 'bot'
									? { background: '#F0FDF4', borderRadius: '4px 14px 14px 14px', alignSelf: 'flex-start', color: '#1A2332' }
									: { background: 'linear-gradient(135deg, #22c55e, #14b8a6)', borderRadius: '14px 4px 14px 14px', alignSelf: 'flex-end', color: '#fff', marginLeft: 'auto' }),
							}}>
								{msg.text}
							</Box>
							{isLastBotMsg && (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
									{msg.questionData.options.map((opt, oi) => (
										<Button
											key={oi}
											variant="outlined"
											size="small"
											onClick={() => handleGenericChoice(opt)}
											sx={{
												textTransform: 'none', borderRadius: '10px', fontSize: 12,
												borderColor: '#14b8a6', color: '#1A2332',
												'&:hover': { borderColor: '#22c55e', background: '#f0fdf4' },
											}}
										>
											{opt}
										</Button>
									))}
								</Box>
							)}
						</Box>
					);
				})}

				{/* Typing indicator */}
				{genericTyping && (
					<Box sx={{ display: 'flex', gap: '4px', padding: '10px 14px', background: '#F0FDF4', borderRadius: '4px 14px 14px 14px', alignSelf: 'flex-start' }}>
						{[0, 1, 2].map((i) => (
							<Box key={i} sx={{
								width: 6, height: 6, background: '#22c55e', borderRadius: '50%',
								animation: 'typingBounce 1.4s infinite', animationDelay: `${i * 0.2}s`,
								'@keyframes typingBounce': {
									'0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
									'30%': { opacity: 1, transform: 'translateY(-4px)' },
								},
							}} />
						))}
					</Box>
				)}

				{/* Join Queue button */}
				{genericDone && !queueJoined && (
					<Box sx={{ alignSelf: 'flex-start', mt: 1 }}>
						<Button
							variant="contained"
							startIcon={<QueueIcon />}
							onClick={handleJoinQueue}
							sx={{
								background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
								textTransform: 'none', borderRadius: '12px', fontWeight: 600, fontSize: 13,
								'&:hover': { background: 'linear-gradient(135deg, #16a34a, #0d9488, #7c3aed)' },
							}}
						>
							Join Queue for Counsellor
						</Button>
					</Box>
				)}

				<div ref={genericEndRef} />
			</Box>

			{/* Input - only for open questions */}
			{!genericDone && genericQuestions[genericQuestionIdx]?.type === 'open' && (
				<Box sx={{ display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid rgba(0,0,0,0.06)', background: '#FAFCFB' }}>
					<input
						value={genericInput}
						onChange={(e) => setGenericInput(e.target.value)}
						onKeyDown={(e) => { if (e.key === 'Enter') handleGenericSend(); }}
						placeholder="Type your message..."
						style={{
							flex: 1, padding: '10px 14px', border: '2px solid rgba(0,0,0,0.08)',
							borderRadius: '12px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
							outline: 'none', transition: 'border-color 0.2s',
						}}
						onFocus={(e) => (e.target.style.borderColor = '#22c55e')}
						onBlur={(e) => (e.target.style.borderColor = 'rgba(0,0,0,0.08)')}
					/>
					<Button
						onClick={handleGenericSend}
						disabled={!genericInput.trim()}
						sx={{
							padding: '10px 18px', background: '#22c55e', color: '#fff', border: 'none',
							borderRadius: '12px', fontSize: 13, fontWeight: 700, textTransform: 'none', minWidth: 'auto',
							'&:hover': { background: '#16a34a' },
							'&:disabled': { background: '#ccc', color: '#888' },
						}}
					>
						<SendIcon sx={{ fontSize: 18 }} />
					</Button>
				</Box>
			)}
		</Paper>
	);

	// ============== LIVE CHAT VIEW ==============
	const renderLiveChat = () => (
		<Paper elevation={0} sx={{
			width: '100%', maxWidth: 700, height: '80vh', display: 'flex', flexDirection: 'column',
			borderRadius: '20px', overflow: 'hidden', mx: 'auto',
			background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
		}}>
			{/* Header */}
			<Box sx={{
				padding: '14px 22px',
				background: 'linear-gradient(135deg, #22c55e, #14b8a6, #8b5cf6)',
				color: '#fff', display: 'flex', alignItems: 'center', gap: '10px',
			}}>
				<Box sx={{
					width: 38, height: 38, background: 'rgba(255,255,255,0.2)',
					borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
				}}>
					<SupportAgentIcon sx={{ fontSize: 20, color: '#fff' }} />
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, fontWeight: 700 }}>
						{liveChatDoctor?.name}
					</Typography>
					<Typography sx={{ fontSize: 11, opacity: 0.85 }}>
						<Box component="span" sx={{ width: 7, height: 7, background: '#4ADE80', borderRadius: '50%', display: 'inline-block', mr: 0.5 }} />
						Live chat · Confidential & safe
					</Typography>
				</Box>
				<IconButton onClick={handleCloseLiveChat} sx={{ color: '#fff' }}>
					<ArrowBackIcon />
				</IconButton>
			</Box>

			{/* Messages */}
			<Box sx={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: '12px', background: '#FAFCFB' }}>
				{/* {liveChatMessages.map((msg, idx) => (
					<Box key={idx} sx={{
						maxWidth: '80%', padding: '11px 15px', fontSize: 13, lineHeight: 1.5,
						animation: 'chatFadeIn 0.3s ease-out',
						'@keyframes chatFadeIn': {
							from: { opacity: 0, transform: 'translateY(14px)' },
							to: { opacity: 1, transform: 'translateY(0)' },
						},
						...(msg.from === 'bot'
							? { background: '#F0FDF4', borderRadius: '4px 14px 14px 14px', alignSelf: 'flex-start', color: '#1A2332' }
							: { background: 'linear-gradient(135deg, #22c55e, #14b8a6)', borderRadius: '14px 4px 14px 14px', alignSelf: 'flex-end', color: '#fff' }),
					}} >
						{msg.text}
					</Box>
				))} */}
			{liveChatMessages.map((msg, idx) => {
	const messageDate = parseServerTimestamp(msg.timestamp);

	const time = messageDate.toLocaleTimeString("en-IN", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	}).toLowerCase();

	const currentDate = messageDate.toDateString();
	const prevDate =
		idx > 0
			? parseServerTimestamp(liveChatMessages[idx - 1].timestamp).toDateString()
			: null;

	const showDateDivider = currentDate !== prevDate;

					const today = new Date().toDateString();
					const yesterday = new Date(Date.now() - 86400000).toDateString();

					let displayDate = messageDate.toDateString();

					if (currentDate === today) displayDate = "Today";
					else if (currentDate === yesterday) displayDate = "Yesterday";
					else
						displayDate = messageDate.toLocaleDateString("en-IN", {
							day: "numeric",
							month: "long",
							year: "numeric",
						});

					return (
						<React.Fragment key={idx}>
							{/* Date Divider */}
							{showDateDivider && (
								<Box
									sx={{
										alignSelf: "center",
										bgcolor: "#E5E7EB",
										color: "#444",
										px: 2,
										py: "3px",
										borderRadius: "8px",
										fontSize: "11px",
										my: 1,
									}}
								>
									{displayDate}
								</Box>
							)}

							{/* Message Bubble */}
							<Box
								sx={{
									maxWidth: "75%",
									px: "12px",
									py: "8px",
									fontSize: 13,
									display: "inline-flex",
									alignItems: "flex-end",
									gap: "6px",
									wordBreak: "break-word",
									...(msg.from === "bot"
										? {
											background: "#F0FDF4",
											borderRadius: "4px 14px 14px 14px",
											alignSelf: "flex-start",
											color: "#1A2332",
										}
										: {
											background:
												"linear-gradient(135deg,#22c55e,#14b8a6)",
											borderRadius: "14px 4px 14px 14px",
											alignSelf: "flex-end",
											color: "#fff",
										}),
								}}
							>
								<span>{msg.text}</span>

								<span
									style={{
										fontSize: "10px",
										opacity: 0.7,
										whiteSpace: "nowrap",
									}}
								>
									{time}
								</span>
							</Box>
						</React.Fragment>
					);
				})}
				{liveChatTyping && (
					<Box sx={{ display: 'flex', gap: '4px', padding: '10px 14px', background: '#F0FDF4', borderRadius: '4px 14px 14px 14px', alignSelf: 'flex-start' }}>
						{[0, 1, 2].map((i) => (
							<Box key={i} sx={{
								width: 6, height: 6, background: '#22c55e', borderRadius: '50%',
								animation: 'typingBounce 1.4s infinite', animationDelay: `${i * 0.2}s`,
								'@keyframes typingBounce': {
									'0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
									'30%': { opacity: 1, transform: 'translateY(-4px)' },
								},
							}} />
						))}
					</Box>
				)}
				<div ref={liveChatEndRef} />
			</Box>

			{/* Input */}
			<Box sx={{ display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid rgba(0,0,0,0.06)', background: '#FAFCFB' }}>
				<input
					value={liveChatInput}
					onChange={(e) => {
						setLiveChatInput(e.target.value);
						// Send typing indicator (throttled to once per 2 seconds)
						if (!typingSendTimeoutRef.current && messagingWebSocket.isConnected()) {
							messagingWebSocket.sendTyping();
							typingSendTimeoutRef.current = setTimeout(() => {
								typingSendTimeoutRef.current = null;
							}, 2000);
						}
					}}
					onKeyDown={(e) => { if (e.key === 'Enter') handleSendLiveChat(); }}
					placeholder="Type your message..."
					style={{
						flex: 1, padding: '10px 14px', border: '2px solid rgba(0,0,0,0.08)',
						borderRadius: '12px', fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
						outline: 'none', transition: 'border-color 0.2s',
					}}
					onFocus={(e) => (e.target.style.borderColor = '#22c55e')}
					onBlur={(e) => (e.target.style.borderColor = 'rgba(0,0,0,0.08)')}
				/>
				<Button
					onClick={handleSendLiveChat}
					disabled={!liveChatInput.trim()}
					sx={{
						padding: '10px 18px', background: '#22c55e', color: '#fff', border: 'none',
						borderRadius: '12px', fontSize: 13, fontWeight: 700, textTransform: 'none', minWidth: 'auto',
						'&:hover': { background: '#16a34a' },
						'&:disabled': { background: '#ccc', color: '#888' },
					}}
				>
					<SendIcon sx={{ fontSize: 18 }} />
				</Button>
			</Box>
		</Paper>
	);

	return (
		<Box sx={{ background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', }}>
			<Container maxWidth="lg" sx={{
				py: 4,
				minHeight: '100vh',
				background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
			}}>
				{liveChatOpen ? renderLiveChat() :
					genericChatOpen ? renderGenericChat() :
						showLobby && renderLobby()
				}
			</Container>

			{/* New message notification popup */}
			<Snackbar
				open={newMessageAlert}
				autoHideDuration={4000}
				onClose={() => setNewMessageAlert(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			>
				<Alert
					onClose={() => setNewMessageAlert(false)}
					severity="info"
					variant="filled"
					sx={{
						background: 'linear-gradient(135deg, #22c55e, #14b8a6)',
						color: '#fff',
						fontWeight: 600,
						fontSize: 13,
						borderRadius: '12px',
						boxShadow: '0 4px 20px rgba(34,197,94,0.3)',
					}}
				>
					💬 Counsellor replied: {newMessageText?.length > 50 ? newMessageText.slice(0, 50) + '...' : newMessageText}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default Converse;
